import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Form, InputNumber } from 'antd';
import PartEquipmentPricePicker from './PartEquipmentPricePicker';
import { nullSafeGet, roundOffToDecimal } from '../../utils/DataAccessUtils';
import { connect } from 'react-redux';

interface PartEquipmentCostFormFieldProps {
	currentUser?: any;
	unitCost: Number;
	entityId: Number;
	entityName: string;
	vendorId: Number;
	fetchMethod: any;
	setFieldsValue: any;
	form: any;
	currencyId: any;
	quantity: number;
	displayTotalCostOnlyWithValue?: boolean;
}

const PartEquipmentCostFormField: FC<PartEquipmentCostFormFieldProps> = ({
	entityId,
	entityName,
	vendorId,
	fetchMethod,
	setFieldsValue,
	form,
	currencyId,
	quantity,
	displayTotalCostOnlyWithValue,
}) => {
	const unitCost = Form.useWatch('unitCost', form);

	const [catalogCurrencyId, setCatalogCurrencyId] = useState(currencyId);
	const [selectedCost, setSelectedCost] = useState(null);

	useEffect(() => {
		setFieldsValue({
			cost: roundOffToDecimal((unitCost || 0) * (quantity || 0)),
		});
	}, [unitCost, quantity, setFieldsValue]);

	const onPriceSelect = useCallback(
		(nextUnitCost) => {
			setSelectedCost(nextUnitCost);
			setFieldsValue({
				unitCost: nextUnitCost ? roundOffToDecimal(nextUnitCost) : nextUnitCost,
			});
		},
		[setFieldsValue]
	);

	const onCatalogLoad = useCallback(
		(catalog, nextCatalogCurrencyId) => {
			setCatalogCurrencyId(nextCatalogCurrencyId || currencyId);
			const currentUnitCost = form.getFieldValue('unitCost');
			if (currentUnitCost) {
				const listPrice = nullSafeGet('listPrice', catalog);
				const lastPrice = nullSafeGet('lastPrice', catalog);
				if (listPrice && parseFloat(listPrice) === parseFloat(currentUnitCost)) {
					setSelectedCost(parseFloat(listPrice));
				} else if (lastPrice && parseFloat(lastPrice) === parseFloat(currentUnitCost)) {
					setSelectedCost(parseFloat(lastPrice));
				}
			}
		},
		[currencyId, form]
	);

	const canShowTotalCost = useMemo(
		() => !displayTotalCostOnlyWithValue || (quantity && unitCost),
		[displayTotalCostOnlyWithValue, quantity, unitCost]
	);

	return (
		<>
			<Form.Item
				name="unitCost"
				label={
					<div className="flex flex-row items-center">
						Unit Cost
						<PartEquipmentPricePicker
							entityId={entityId}
							entityName={entityName as 'partId' | 'equipmentTypeId'}
							vendorId={vendorId}
							fetchMethod={fetchMethod}
							fallbackCurrencyId={currencyId}
							value={selectedCost}
							onChange={onPriceSelect}
							onCatalogLoad={onCatalogLoad}
						/>
					</div>
				}
				className="w-100"
			>
				<InputNumber style={{ width: '100%' }} addonAfter={catalogCurrencyId} />
			</Form.Item>
			{canShowTotalCost && (
				<Form.Item name="cost" label="Total Cost">
					<InputNumber disabled step={0.01} style={{ width: '100%' }} addonAfter={currencyId} />
				</Form.Item>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default connect(mapStateToProps)(PartEquipmentCostFormField);
